// AdminDashboard.js
import { useState, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Settings, DollarSign, Cloud, LogOut, X, UserRoundSearch, TextSearch, FileStack } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { urls } from "../services/apiService";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import AssignDeviceDialog from "../components/Modal/AssignDeviceDialog";
import SearchDevicesDialog from "../components/Modal/SearchDevicesDialog";
import SearchDevicesIdDialog from "../components/Modal/SearchDevicesIdDialog";
import PageManagementDialog from "../components/admin/pagemanagement";
import AuthenticatedUsers from "../components/admin/userdisplay";

export default function AdminDashboard() {
  const [cloudPercentage, setCloudPercentage] = useState(0);
  const [pixelPercentage, setPixelPercentage] = useState(0);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("app");
  const [showMap, setShowMap] = useState(false);
  const [telusers, setTelUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPlan, setNewPlan] = useState("Free");
  const [isOpen, setIsOpen] = useState(false);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [diamondPrice, setDiamondPrice] = useState(100);
  const [platinumPrice, setPlatinumPrice] = useState(200);
  const [duration, setDuration] = useState(12);
  const [cloudHistory, setCloudHistory] = useState([]);
  const [pixelHistory, setPixelHistory] = useState([]);
  const [isPixelModalOpen, setIsPixelModalOpen] = useState(false);
  const [isPageManagementModalOpen, setIsPageManagementModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCloudModalOpen, setIsCloudModalOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isdeviceSearchOpen, setIsdeviceSearchOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const navigate = useNavigate();

  // Filter users based on search query
  const filteredUsers = users.filter((user) =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTelUsers = telusers.filter((user) =>
    user.msisdn.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(urls.getUsers);
        const usersWithDefaultPlan = response.data.map(user => ({
          ...user,
          plan: user.plan,
          username: user.username
        }));
        setUsers(usersWithDefaultPlan);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchTelUsers = async () => {
      try {
        const response = await axios.get(urls.getTelUsers);
        const usersMapped = response.data.map(user => ({
          id: user.id,
          msisdn: user.msisdn,
          fieldname: user.fieldname,
          plan: user.plan || "Free",
        }));
        setTelUsers(usersMapped);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchTelUsers();
  }, []);

  const updateUserPlan = (id, newPlan) => {
    setUsers(users.map(user => user.id === id ? { ...user, plan: newPlan } : user));
  };

  const openModal = (user) => {
    setSelectedUser(user);
    setNewPlan(user.plan || "Free");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleSavePlan = async () => {
    if (selectedUser) {
      try {
        setLoading(true);
        const response = await axios.put(urls.updatePlan, {
          id: selectedUser.id,
          plan: newPlan
        });

        if (response.status === 200) {
          updateUserPlan(selectedUser.id, newPlan);
          closeModal();
        } else {
          console.error("Failed to update plan:", response.data.error);
        }
      } catch (error) {
        console.error("Error updating plan:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAssignSuccess = (response) => {
    console.log('Device assigned:', response);
  };

  useEffect(() => {
    fetchCloudHistory();
  }, [cloudHistory, cloudPercentage]);

  useEffect(() => {
    fetchPixelHistory();
  }, [pixelHistory, pixelPercentage]);

  const fetchCloudHistory = async () => {
    try {
      const response = await axios.get(urls.getCloud);
      if (response.status === 200) {
        setCloudHistory(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching cloud allocation history:", error);
    }
  };

  const handleCloudSubmit = async () => {
    try {
      const response = await axios.post(urls.updateCloud, { percentage: cloudPercentage });
      if (response.status === 200) {
        Swal.fire({
          title: "Cloud Allocation Updated!",
          text: `Cloud allocation set to ${cloudPercentage}%.`,
          icon: "success",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Failed to update cloud allocation.",
        icon: "error",
        confirmButtonColor: "#d33",
        confirmButtonText: "Retry",
      });
      console.error("Error updating cloud allocation:", error);
    }
  };

  const fetchPixelHistory = async () => {
    try {
      const response = await axios.get(urls.getPixels);
      if (response.status === 200) {
        setPixelHistory(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching pixel scale history:", error);
    }
  };

  const handlePixelSubmit = async () => {
    try {
      const response = await axios.post(urls.updatePixels, { scale: pixelPercentage });
      if (response.status === 200) {
        Swal.fire({
          title: "Pixel Scale Updated!",
          text: `Pixel scale set to ${pixelPercentage}.`,
          icon: "success",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Failed to update pixel scale.",
        icon: "error",
        confirmButtonColor: "#d33",
        confirmButtonText: "Retry",
      });
      console.error("Error updating pixel scale:", error);
    }
  };

  async function handlePricingSubmit() {
    const data = {
      diamond_price: diamondPrice,
      platinum_price: platinumPrice,
      duration: duration,
    };

    try {
      const response = await fetch(urls.updatePricing, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        Swal.fire({
          title: "Pricing Updated!",
          text: "Subscription pricing has been successfully updated.",
          icon: "success",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "OK",
        });
        setPricingModalOpen(false);
      } else {
        console.error("Failed to update pricing");
      }
    } catch (error) {
      console.error("Error updating pricing:", error);
    }
  }

  const handleLogout = () => {
    Swal.fire({
      title: "Logged Out",
      text: "You have been successfully logged out.",
      icon: "success",
      confirmButtonColor: "#d33",
      confirmButtonText: "OK",
    });
    localStorage.clear();
    navigate("/adminlogin");
  };

  const handlePasswordChange = async (user) => {
    try {
      const res = await axios.post(urls.getUid, {
        email: user.username
      });
      const uid = res.data.uid;
      setSelectedUserForPassword({ ...user, uid });
      setShowPasswordModal(true);
    } catch (error) {
      alert("UID lookup failed: " + (error.response?.data?.error || error.message));
    }
  };

  return (
    <div className="w-[90%] bg-gray-50 p-8 mr-12 text-gray-900 mx-auto mt-6 rounded-xl shadow-xl">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Admin Dashboard</h1>
          <p className="text-gray-500 mt-1">User Management Portal</p>
        </div>
        <div className="flex items-center space-x-4">
          {/* Settings Button */}
          <div className="relative group">
            <button
              onClick={() => setIsOpen(true)}
              className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition duration-300 shadow-md"
            >
              <Settings className="w-6 h-6 text-gray-700" />
            </button>
            {/* Modal */}
            <Transition show={isOpen} as={Fragment}>
              <Dialog
                onClose={() => setIsOpen(false)}
                className="fixed inset-0 z-50 flex items-center justify-center"
              >
                <div className="absolute inset-0 bg-black/50 backdrop-blur-md transition-opacity duration-300"></div>
                <div className="relative bg-gradient-to-br from-white to-gray-50 p-6 sm:p-8 rounded-2xl shadow-2xl w-[90%] max-w-lg mx-auto border border-gray-200 transition-all duration-300">
                  <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-3">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center gap-2">
                      ⚙️ <span>Settings</span>
                    </h2>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="text-gray-500 hover:text-gray-800 transition transform hover:scale-110"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-5">
                    <div
                      className="group flex flex-col items-center bg-white shadow-md p-4 rounded-xl border border-gray-100 hover:shadow-lg hover:border-blue-400 transition-all duration-300 cursor-pointer"
                      onClick={() => setPricingModalOpen(true)}
                    >
                      <DollarSign className="w-7 h-7 text-blue-600 mb-2 group-hover:scale-110 transition-transform" />
                      <span className="text-gray-700 font-medium text-sm sm:text-base text-center">
                        Update Pricing
                      </span>
                    </div>
                    <div
                      className="group flex flex-col items-center bg-white shadow-md p-4 rounded-xl border border-gray-100 hover:shadow-lg hover:border-blue-400 transition-all duration-300 cursor-pointer"
                      onClick={() => setIsCloudModalOpen(true)}
                    >
                      <Cloud className="w-7 h-7 text-blue-600 mb-2 group-hover:scale-110 transition-transform" />
                      <span className="text-gray-700 font-medium text-sm sm:text-base text-center">
                        Cloud: {cloudHistory[0]?.percentage ?? 0}%
                      </span>
                    </div>
                    <div
                      className="group flex flex-col items-center bg-white shadow-md p-4 rounded-xl border border-gray-100 hover:shadow-lg hover:border-blue-400 transition-all duration-300 cursor-pointer"
                      onClick={() => setIsPixelModalOpen(true)}
                    >
                      <Cloud className="w-7 h-7 text-blue-600 mb-2 group-hover:scale-110 transition-transform" />
                      <span className="text-gray-700 font-medium text-sm sm:text-base text-center">
                        Pixels Scale: {pixelHistory[0]?.scale ?? 0}%
                      </span>
                    </div>
                    <div
                      className="group flex flex-col items-center bg-white shadow-md p-4 rounded-xl border border-gray-100 hover:shadow-lg hover:border-blue-400 transition-all duration-300 cursor-pointer"
                      onClick={() => setIsPageManagementModalOpen(true)}
                    >
                      <FileStack className="w-7 h-7 text-blue-600 mb-2 group-hover:scale-110 transition-transform" />
                      <span className="text-gray-700 font-medium text-sm sm:text-base text-center">
                        Page Management
                      </span>
                    </div>
                    <div
                      className="group flex flex-col items-center bg-white shadow-md p-4 rounded-xl border border-gray-100 hover:shadow-lg hover:border-red-400 transition-all duration-300 cursor-pointer"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-7 h-7 text-red-600 mb-2 group-hover:scale-110 transition-transform" />
                      <span className="text-gray-700 font-medium text-sm sm:text-base text-center">
                        Logout
                      </span>
                    </div>
                  </div>
                </div>
              </Dialog>
            </Transition>
          </div>

          {/* Device Search Button */}
          <div className="relative group">
            <button
              onClick={() => setIsdeviceSearchOpen(true)}
              className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition duration-300 shadow-md"
            >
              <TextSearch className="w-6 h-6 text-gray-700" />
            </button>
            <span className="absolute z-10 bottom-full left-1/2 transform -translate-x-1/2 mb-2 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-300 px-2 py-1 text-xs font-medium text-white bg-gray-800 rounded whitespace-nowrap">
              Search Device
              <div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-gray-800 rotate-45 transform mt-[-1px]"></div>
            </span>
          </div>

          {/* User Search Button */}
          <div className="relative group">
            <button
              onClick={() => setIsSearchOpen(true)}
              className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition duration-300 shadow-md"
            >
              <UserRoundSearch className="w-6 h-6 text-gray-700" />
            </button>
            <span className="absolute z-10 bottom-full left-1/2 transform -translate-x-1/2 mb-2 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-300 px-2 py-1 text-xs font-medium text-white bg-gray-800 rounded whitespace-nowrap">
              Search User
              <div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-gray-800 rotate-45 transform mt-[-1px]"></div>
            </span>
          </div>
        </div>
      </div>

      {/* Authenticated Users Component */}
      <AuthenticatedUsers
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        users={activeTab === "app" ? filteredUsers : filteredTelUsers}
        telusers={filteredTelUsers}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        openModal={openModal}
        setIsDialogOpen={setIsDialogOpen}
        setSelectedUser={setSelectedUser}
        handlePasswordChange={handlePasswordChange}
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        selectedUser={selectedUser}
        newPlan={newPlan}
        setNewPlan={setNewPlan}
        loading={loading}
        handleSavePlan={handleSavePlan}
          showMap={showMap}
          setShowMap={setShowMap}
      />

      {/* Pricing Modal */}
      <Transition show={pricingModalOpen} as={Fragment}>
        <Dialog onClose={() => setPricingModalOpen(false)} className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Update Pricing</h2>
              <button onClick={() => setPricingModalOpen(false)} className="text-gray-500 hover:text-gray-700">X</button>
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 font-medium">Diamond Price (PKR)</label>
              <input
                type="number"
                value={diamondPrice}
                onChange={(e) => setDiamondPrice(e.target.value)}
                className="w-full p-2 border rounded-lg"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 font-medium">Platinum Price (PKR)</label>
              <input
                type="number"
                value={platinumPrice}
                onChange={(e) => setPlatinumPrice(e.target.value)}
                className="w-full p-2 border rounded-lg"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 font-medium">Duration (months)</label>
              <input
                type="number"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full p-2 border rounded-lg"
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={handlePricingSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Update
              </button>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Cloud Modal */}
      <Transition show={isCloudModalOpen} as={Fragment}>
        <Dialog onClose={() => setIsCloudModalOpen(false)} className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-3xl flex relative">
              <button
                onClick={() => setIsCloudModalOpen(false)}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="w-1/2 p-4 border-r">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Cloud Allocation History</h2>
                <div className="overflow-y-auto max-h-60">
                  {cloudHistory.length > 0 ? (
                    cloudHistory.map((record) => (
                      <div key={record.id} className="p-2 bg-gray-100 rounded-lg mb-2">
                        <p className="text-gray-700 text-sm">
                          <strong>{record.percentage}%</strong> - {new Date(record.updated_at).toLocaleString()}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500">No records found.</p>
                  )}
                </div>
              </div>
              <div className="w-1/2 p-4 flex flex-col items-center">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Update Cloud Allocation</h2>
                <Cloud className="w-10 h-10 text-blue-600 mb-3" />
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={cloudPercentage}
                  onChange={(e) => setCloudPercentage(e.target.value)}
                  className="w-40 accent-blue-600"
                />
                <span className="text-gray-700 font-medium mt-2">{cloudPercentage}%</span>
                <button
                  onClick={handleCloudSubmit}
                  className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Pixel Modal */}
      <Transition show={isPixelModalOpen} as={Fragment}>
        <Dialog onClose={() => setIsPixelModalOpen(false)} className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-3xl flex relative">
              <button
                onClick={() => setIsPixelModalOpen(false)}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="w-1/2 p-4 border-r">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Pixel Allocation History</h2>
                <div className="overflow-y-auto max-h-60">
                  {pixelHistory.length > 0 ? (
                    pixelHistory.map((record) => (
                      <div key={record.id} className="p-2 bg-gray-100 rounded-lg mb-2">
                        <p className="text-gray-700 text-sm">
                          <strong>{record.scale}%</strong> - {new Date(record.updated_at).toLocaleString()}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500">No records found.</p>
                  )}
                </div>
              </div>
              <div className="w-1/2 p-4 flex flex-col items-center">
                <h2 className="text-lg font-semibold text-gray-800 mb-10">Update Pixel Allocation</h2>
                <Cloud className="w-10 h-10 text-blue-600 mb-16" />
                <div className="flex space-x-6 mb-4">
                  {[1, 5, 10].map((value) => (
                    <label key={value} className="flex flex-col items-center">
                      <input
                        type="radio"
                        name="pixelPercentage"
                        value={value}
                        checked={pixelPercentage === value}
                        onChange={() => setPixelPercentage(value)}
                        className="mb-1 accent-blue-600"
                      />
                      <span className="text-gray-700 font-medium">{value}%</span>
                    </label>
                  ))}
                </div>
                <button
                  onClick={handlePixelSubmit}
                  className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-sm">
            <h3 className="text-lg font-semibold mb-4">Change Password</h3>
            <input
              type="password"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded mb-4"
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="px-4 py-2 rounded bg-gray-300 text-gray-700 hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setLoading(true);
                  try {
                    await axios.post(urls.updatePassword, {
                      uid: selectedUserForPassword.uid,
                      new_password: newPassword
                    });
                    Swal.fire("Password updated!");
                    setShowPasswordModal(false);
                    setNewPassword("");
                    setSelectedUserForPassword(null);
                  } catch (err) {
                    alert("Error: " + (err.response?.data?.error || err.message));
                  } finally {
                    setLoading(false);
                  }
                }}
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-500"
              >
                {loading ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      <AssignDeviceDialog
        isOpen={isDialogOpen}
        user={selectedUser}
        onClose={() => setIsDialogOpen(false)}
        onAssign={handleAssignSuccess}
      />
      <SearchDevicesDialog
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
      <SearchDevicesIdDialog
        isOpen={isdeviceSearchOpen}
        onClose={() => setIsdeviceSearchOpen(false)}
      />
      <PageManagementDialog
        isOpen={isPageManagementModalOpen}
        onClose={() => setIsPageManagementModalOpen(false)}
      />
    </div>
  );
}